<template>
  <bulk-update-wrapper
    :saving="saving"
    title="Update Details"
    :button-label="props.buttonLabel"
    :has-changes="hasChanges"
    :show-hint="showHint"
    @save="save"
    @dismiss="dismiss"
  >
    <vs-text-input v-model="details" label="Details" multiline data-test="bulk-update-details" />

    <template #error-snackbar>
      <vs-snackbar :value="error" type="error" sticky @input="clearSnackbar">
        Failed to update details
      </vs-snackbar>
    </template>
  </bulk-update-wrapper>
</template>

<script setup lang="ts">
import BulkUpdateWrapper from '../common/BulkUpdateWrapper.vue'
import { computed, ref, onMounted } from 'vue'
import { JobJson } from '@/models/types/job-json'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import { bulkUpdateDetails } from '@/api/jobs'

const emits = defineEmits<{
  (e: 'dismiss')
  (e: 'updated', { message, updatedJobs }: BulkUpdateResponse)
}>()

const props = defineProps<{
  buttonLabel: string
  selectedJobs: JobJson[]
}>()

const error = ref(false)
const saving = ref(false)
const details = ref<string | undefined>()
let initialDetails: string | undefined

onMounted(() => {
  const selectedDetails: Set<string> = new Set(props.selectedJobs.map((job) => job.details))
  if (selectedDetails.size === 1) {
    initialDetails = [...selectedDetails][0]
  }
  details.value = initialDetails
})

const hasChanges = computed<boolean>(() => {
  return details.value !== initialDetails
})
const showHint = computed<boolean>(() => {
  return !details.value && initialDetails === undefined
})

const clearSnackbar = () => (error.value = false)
const dismiss = () => emits('dismiss')

const save = async () => {
  saving.value = true
  try {
    await bulkUpdateDetails(
      props.selectedJobs.map((job) => job.number),
      details.value ?? '',
    )
    const updatedJobs: JobJson[] = props.selectedJobs.map(({ ...job }) =>
      Object.assign(job, { details: details.value }),
    )
    emits('updated', { message: 'Details updated', updatedJobs })
  } catch (err) {
    error.value = true
  } finally {
    saving.value = false
  }
}
</script>
